import { AnalyticsProvider } from './base';
import type { UserProperties, PageViewData, PentictonEventsTracking } from '../types';

declare global {
  interface Window {
    plausible: (eventName: string, options?: { props?: Record<string, any> }) => void;
  }
}

export class PlausibleProvider extends AnalyticsProvider {
  private domain: string;
  private apiHost: string;
  private initialized: boolean = false;

  constructor(
    domain: string,
    apiHost: string = 'https://plausible.io',
    enabled: boolean = true,
    debug: boolean = false,
  ) {
    super(enabled, debug);
    this.domain = domain;
    this.apiHost = apiHost;
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.domain) {
      return;
    }

    try {
      // Load Plausible script
      const script = document.createElement('script');
      script.defer = true;
      script.setAttribute('data-domain', this.domain);
      script.setAttribute('data-api', `${this.apiHost}/api/event`);
      script.src = `${this.apiHost}/js/script.js`;
      document.head.appendChild(script);

      // Wait for script to load
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
      });

      this.initialized = true;
      this.log('Plausible initialized', { domain: this.domain, apiHost: this.apiHost });
    } catch (error) {
      console.error('Failed to initialize Plausible:', error);
    }
  }

  trackEvent<T extends keyof PentictonEventsTracking>(
    eventName: T,
    properties: PentictonEventsTracking[T],
  ): void {
    if (!this.shouldTrack() || !this.initialized || !window.plausible) {
      this.log('Event tracking skipped', { eventName, properties });
      return;
    }

    try {
      // Plausible expects simplified event names and properties
      const simplifiedEventName = this.mapEventName(eventName as string);
      const simplifiedProps = this.simplifyProperties(properties);

      window.plausible(simplifiedEventName, {
        props: simplifiedProps,
      });

      this.log('Event tracked', {
        eventName: simplifiedEventName,
        properties: simplifiedProps,
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  trackPageView(data: PageViewData): void {
    if (!this.shouldTrack() || !this.initialized) {
      this.log('Page view tracking skipped', data);
      return;
    }

    try {
      // Plausible automatically tracks page views, but we can send custom ones
      if (window.plausible) {
        window.plausible('pageview', {
          props: {
            title: data.page_title,
            url: data.page_location,
          },
        });
      }

      this.log('Page view tracked', data);
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  setUserProperties(properties: UserProperties): void {
    // Plausible doesn't support user properties in the same way as GA4
    // We can store them for use in subsequent events
    if (properties.userId) {
      this.log(
        "User properties noted (Plausible doesn't store user properties)",
        properties,
      );
    }
  }

  setConsent(granted: boolean): void {
    this.consentGranted = granted;
    this.log('Consent updated (Plausible is privacy-first by default)', { granted });
  }

  private mapEventName(eventName: string): string {
    // Convert snake_case to Title Case for better readability in Plausible
    return eventName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private simplifyProperties(properties: any): Record<string, string | number> {
    const simplified: Record<string, string | number> = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'string' || typeof value === 'number') {
          simplified[key] = value;
        } else if (typeof value === 'boolean') {
          simplified[key] = value ? 'true' : 'false';
        } else {
          simplified[key] = JSON.stringify(value);
        }
      }
    });

    return simplified;
  }
}
